"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import { apiClient } from "@/lib/apiClient";
import { useAnalyticsUserProps } from "@/hooks/useAnalyticsUserProps";
import { trackEvent } from "@/lib/analytics";
import type { Severity } from "@/lib/announcementBannerTypes";

interface AnnouncementBanner {
    title?: string;
    message: string;
    severity: Severity;
    ctaLabel?: string;
    ctaUrl?: string;
}

const DISMISSED_KEY = "announcement_banner_dismissed";

// Per Figma: only CRITICAL gets the bold solid-fill treatment (theme.ts's `error.main`).
// INFO/WARNING stay pale-tinted, sourced from theme.ts's `alert` palette — the same
// error/warning/color+background tokens the standard MuiAlert variants use.
const SEVERITY_BG: Record<Severity, string> = {
    INFO: "alert.info.background",
    WARNING: "alert.warning.background",
    CRITICAL: "error.main",
};

// CRITICAL's text reuses `alert.error.background` (the pale pink of a *standard* error alert)
// as its own off-white text tint on top of the solid fill — an intentional Figma color reuse,
// not a copy-paste mistake.
const SEVERITY_TEXT: Record<Severity, string> = {
    INFO: "alert.info.color",
    WARNING: "alert.warning.color",
    CRITICAL: "alert.error.background",
};

// WARNING's icon is accented orange, distinct from its dark teal text; INFO/CRITICAL icons match their text color.
const SEVERITY_ICON_COLOR: Record<Severity, string> = {
    INFO: "inherit",
    WARNING: "warning.dark",
    CRITICAL: "inherit",
};

const SEVERITY_ICON = {
    INFO: <InfoRoundedIcon />,
    WARNING: <ReportProblemRoundedIcon />,
    CRITICAL: <ErrorRoundedIcon />,
};

// The "Ok, got it" button only ever renders for CRITICAL, so its text color is pinned
// directly to error.main rather than looked up via SEVERITY_BG[banner.severity].
const CRITICAL_BG = SEVERITY_BG.CRITICAL;

function isExternalUrl(url: string): boolean {
    return url.startsWith("http://") || url.startsWith("https://");
}

export const BANNER_UPDATED_EVENT = "announcement-banner-updated";

function fetchBanner(
    setBanner: (b: AnnouncementBanner | null) => void,
    setDismissed: (d: boolean) => void,
) {
    apiClient
        .get<{ success: boolean; data: AnnouncementBanner | null }>("/customer-portal/announcement-banner")
        .then((res) => {
            setBanner(res.data ?? null);
            setDismissed(!res.data);
        })
        .catch(() => {
            setBanner(null);
            setDismissed(true);
        });
}

export function AnnouncementBanner() {
    const [banner, setBanner] = useState<AnnouncementBanner | null>(null);
    const [dismissed, setDismissed] = useState(true);
    const router = useRouter();
    const analyticsUserProps = useAnalyticsUserProps();

    useEffect(() => {
        if (sessionStorage.getItem(DISMISSED_KEY) === "true") return;
        fetchBanner(setBanner, setDismissed);
    }, []);

    useEffect(() => {
        function handleUpdate() {
            sessionStorage.removeItem(DISMISSED_KEY);
            fetchBanner(setBanner, setDismissed);
        }
        globalThis.addEventListener(BANNER_UPDATED_EVENT, handleUpdate);
        return () => globalThis.removeEventListener(BANNER_UPDATED_EVENT, handleUpdate);
    }, []);

    if (dismissed || !banner) return null;

    const showCta = !!(banner.ctaLabel && banner.ctaUrl);
    const isCritical = banner.severity === "CRITICAL";

    function handleDismiss() {
        if (!banner) return;
        sessionStorage.setItem(DISMISSED_KEY, "true");
        setDismissed(true);
        trackEvent("announcement_dismissed", {
            severity: banner.severity,
            has_cta: showCta,
            ...analyticsUserProps(),
        });
    }

    function handleCta() {
        if (!banner?.ctaUrl) return;
        trackEvent("announcement_cta_clicked", {
            cta_label: banner.ctaLabel,
            cta_url: banner.ctaUrl,
            ...analyticsUserProps(),
        });
        if (isExternalUrl(banner.ctaUrl)) {
            window.open(banner.ctaUrl, "_blank", "noopener,noreferrer");
        } else {
            router.push(banner.ctaUrl);
        }
    }

    return (
        // overflow hidden clips the banner while it's transformed up over the AppBar during
        // the enter transition, so it reveals from underneath instead of sliding over it.
        <Box className="announcement-banner" sx={{ overflow: "hidden", flexShrink: 0 }}>
            <Slide direction="down" in appear>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        px: 3,
                        py: 1.5,
                        bgcolor: SEVERITY_BG[banner.severity],
                        color: SEVERITY_TEXT[banner.severity],
                        borderRadius: "0 0 12px 12px",
                    }}
                >
                    <Box sx={{ display: "flex", color: SEVERITY_ICON_COLOR[banner.severity] }}>
                        {SEVERITY_ICON[banner.severity]}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {banner.title && <Typography sx={{ fontWeight: 600 }}>{banner.title}</Typography>}
                        <Typography variant="body2">{banner.message}</Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                        {showCta && (
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={handleCta}
                                sx={{ whiteSpace: "nowrap" }}
                            >
                                {banner.ctaLabel}
                            </Button>
                        )}
                        {isCritical ? (
                            <Button
                                variant="contained"
                                onClick={handleDismiss}
                                endIcon={<CloseIcon />}
                                sx={{
                                    bgcolor: "common.white",
                                    color: CRITICAL_BG,
                                    whiteSpace: "nowrap",
                                    "&:hover": { bgcolor: "common.white" },
                                }}
                            >
                                Ok, got it
                            </Button>
                        ) : (
                            <IconButton
                                aria-label="dismiss announcement"
                                onClick={handleDismiss}
                                sx={{ color: "inherit" }}
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </Box>
                </Box>
            </Slide>
        </Box>
    );
}
